import { useCallback, useEffect, useState } from 'react';
import {
  loadDocuments,
  removeDocument,
  renameDocumentRequest,
  requestUploadLink,
  type UsePropertyDocumentsState,
} from './property-documents-api';

export type { PropertyDocument } from './property-document.types';

/**
 * Manages loading, uploading, renaming and deleting a property's documents.
 *
 * @param agencyId - Owning agency identifier
 * @param propertyId - Target property identifier
 * @returns Document list, load flags and mutation callbacks
 */
export function usePropertyDocuments(agencyId: string, propertyId: string) {
  const [state, setState] = useState<UsePropertyDocumentsState>({
    documents: [],
    loading: true,
    error: null,
  });

  const fetchDocuments = useCallback(
    () => loadDocuments(agencyId, propertyId, setState),
    [agencyId, propertyId],
  );

  const getUploadLink = useCallback(
    (filename: string, type: string) =>
      requestUploadLink(agencyId, propertyId, filename, type),
    [agencyId, propertyId],
  );

  const deleteDocument = useCallback(
    (documentId: string) => removeDocument(agencyId, propertyId, documentId, setState),
    [agencyId, propertyId],
  );

  const renameDocument = useCallback(
    (documentId: string, filename: string) =>
      renameDocumentRequest(agencyId, propertyId, documentId, filename, setState),
    [agencyId, propertyId],
  );

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  return {
    ...state,
    refetch: fetchDocuments,
    getUploadLink,
    deleteDocument,
    renameDocument,
  };
}
